import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Building2, Upload, Trash2, Loader2, Save, Globe, Linkedin, Eye,
    MapPin, Users, Calendar, Mail, Phone, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Company profile editor.
 *
 * Everything here is candidate-facing — it renders on public job pages — so the
 * copy leans on that rather than describing the fields in the abstract.
 */

const COMPANY_SIZES = [
    { value: '',          label: 'Not specified' },
    { value: '1-10',      label: '1–10 employees' },
    { value: '11-50',     label: '11–50 employees' },
    { value: '51-200',    label: '51–200 employees' },
    { value: '201-500',   label: '201–500 employees' },
    { value: '501-1000',  label: '501–1,000 employees' },
    { value: '1000+',     label: '1,000+ employees' },
];

const ABOUT_MAX = 5000;

const EMPTY = {
    about: '', website: '', industry: '', companySize: '', location: '',
    publicContactEmail: '', publicPhone: '', linkedIn: '', foundedYear: '',
};

/** Absolute URL for a stored logo path. */
function logoSrc(logoUrl) {
    if (!logoUrl) return null;
    return logoUrl.startsWith('http') ? logoUrl : `${API_CONFIG.STATIC_BASE_URL}${logoUrl}`;
}

function Field({ id, label, icon: Icon, hint, children }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="flex items-center gap-1.5">
                {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
                {label}
            </Label>
            {children}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
        </div>
    );
}


/**
 * Live rendering of the company block as it appears at the top of a public job
 * page. Reads the in-progress form state, so an admin sees the effect of a
 * change while typing rather than having to save and open a posting.
 */
function JobPagePreview({ companyName, form, logoUrl }) {
    const src = logoSrc(logoUrl);
    const monogram = (companyName || '')
        .split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

    const facts = [
        form.industry    && { icon: Building2, label: form.industry },
        form.companySize && { icon: Users,     label: `${form.companySize} employees` },
        form.location    && { icon: MapPin,    label: form.location },
        form.foundedYear && { icon: Calendar,  label: `Founded ${form.foundedYear}` },
    ].filter(Boolean);

    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
            {/* Browser chrome, so it reads as an external page rather than more form */}
            <div className="flex items-center gap-1.5 border-b border-border bg-muted/50 px-3 py-2">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/25" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/25" />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/25" />
                <span className="ml-2 truncate font-mono text-[10px] text-muted-foreground">
                    /jobs/your-open-role
                </span>
            </div>

            <div className="space-y-4 p-4">
                <div className="flex items-center gap-2.5">
                    {src ? (
                        <img src={src} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-border bg-background object-contain p-1" />
                    ) : (
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-bold text-primary ring-1 ring-primary/15">
                            {monogram}
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">
                            {companyName || 'Your company'}
                        </p>
                        <p className="text-[11px] text-muted-foreground">is hiring</p>
                    </div>
                </div>

                <div>
                    <p className="text-base font-semibold leading-tight text-foreground">Senior Backend Engineer</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Example role &middot; posted today</p>
                </div>

                {(form.about || facts.length > 0) && (
                    <div className="space-y-2.5 border-t border-border pt-3">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            About {companyName || 'your company'}
                        </p>
                        {form.about
                            ? <p className="line-clamp-4 text-xs leading-relaxed text-muted-foreground">{form.about}</p>
                            : <p className="text-xs italic text-muted-foreground/60">Your description appears here.</p>}
                        {facts.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {facts.map((f, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                        <f.icon className="h-2.5 w-2.5" />{f.label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {(form.website || form.linkedIn) && (
                            <div className="flex flex-wrap gap-3 pt-0.5">
                                {form.website  && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary"><Globe className="h-2.5 w-2.5" />Visit website</span>}
                                {form.linkedIn && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary"><Linkedin className="h-2.5 w-2.5" />LinkedIn</span>}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function CompanyProfileSection() {
    const [companyName, setCompanyName] = useState('');
    const [form,        setForm]        = useState(EMPTY);
    const [logoUrl,     setLogoUrl]     = useState('');
    const [loading,     setLoading]     = useState(true);
    const [saving,      setSaving]      = useState(false);
    const [uploading,   setUploading]   = useState(false);
    const [dirty,       setDirty]       = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiClient.getCompanyProfile();
                if (cancelled) return;
                const p = res.data?.profile || {};
                setCompanyName(res.data?.companyName || '');
                setLogoUrl(p.logoUrl || '');
                setForm({
                    about:              p.about              || '',
                    website:            p.website            || '',
                    industry:           p.industry           || '',
                    companySize:        p.companySize        || '',
                    location:           p.location           || '',
                    publicContactEmail: p.publicContactEmail || '',
                    publicPhone:        p.publicPhone        || '',
                    linkedIn:           p.linkedIn           || '',
                    foundedYear:        p.foundedYear ?? '',
                });
            } catch (err) {
                if (!cancelled) toast.error(err.message || 'Could not load the company profile.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const setField = (key) => (e) => {
        setForm(prev => ({ ...prev, [key]: e.target.value }));
        setDirty(true);
    };

    const handleSave = async () => {
        if (!companyName.trim()) { toast.error('Company name is required.'); return; }
        setSaving(true);
        try {
            await apiClient.updateCompanyProfile({ companyName: companyName.trim(), profile: form });
            setDirty(false);
            toast.success('Company profile saved.');
        } catch (err) {
            toast.error(err.message || 'Could not save the company profile.');
        } finally {
            setSaving(false);
        }
    };

    const pickLogo = useCallback(async (file) => {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Logo must be 2 MB or smaller.');
            return;
        }
        setUploading(true);
        try {
            const res = await apiClient.uploadCompanyLogo(file);
            setLogoUrl(res.data.logoUrl);
            toast.success('Logo updated.');
        } catch (err) {
            toast.error(err.message || 'Could not upload the logo.');
        } finally {
            setUploading(false);
        }
    }, []);

    const removeLogo = async () => {
        setUploading(true);
        try {
            await apiClient.deleteCompanyLogo();
            setLogoUrl('');
            toast.success('Logo removed.');
        } catch (err) {
            toast.error(err.message || 'Could not remove the logo.');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-11 animate-pulse rounded-lg bg-muted/50" />
                ))}
            </div>
        );
    }

    const src = logoSrc(logoUrl);
    const monogram = companyName.split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

    return (
        <div className="flex flex-col gap-8 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1 space-y-8">
            {/* ── Identity ──────────────────────────────────────────────── */}
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Identity</h3>
                    <p className="text-xs text-muted-foreground">
                        Shown to candidates on your public job pages and in the emails they receive.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-muted/20 p-4">
                    {src ? (
                        <img src={src} alt={companyName}
                            className="h-16 w-16 shrink-0 rounded-xl border border-border bg-card object-contain p-1.5" />
                    ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary ring-1 ring-primary/15">
                            {monogram}
                        </div>
                    )}
                    <div className="min-w-0 flex-1 space-y-2">
                        <p className="text-xs font-medium text-foreground">Company logo</p>
                        <p className="text-[11px] text-muted-foreground">
                            PNG, JPG, WEBP or SVG · up to 2 MB. Without one, candidates see your initials.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
                                onClick={() => fileRef.current?.click()} disabled={uploading}>
                                {uploading
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : src ? <ImageIcon className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
                                {src ? 'Replace' : 'Upload logo'}
                            </Button>
                            {src && (
                                <Button type="button" variant="ghost" size="sm"
                                    className="h-8 gap-1.5 text-xs text-destructive hover:text-destructive"
                                    onClick={removeLogo} disabled={uploading}>
                                    <Trash2 className="h-3.5 w-3.5" />Remove
                                </Button>
                            )}
                        </div>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="sr-only"
                        onChange={e => { pickLogo(e.target.files?.[0]); e.target.value = ''; }}
                    />
                </div>

                <Field id="company-name" label="Company name" icon={Building2}>
                    <Input id="company-name" value={companyName} className="h-11"
                        onChange={e => { setCompanyName(e.target.value); setDirty(true); }} />
                </Field>
            </section>

            {/* ── About ─────────────────────────────────────────────────── */}
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">About us</h3>
                    <p className="text-xs text-muted-foreground">
                        Appears on every public job posting — this is what tells a candidate who you are.
                    </p>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="about">Description</Label>
                    <textarea
                        id="about"
                        rows={7}
                        value={form.about}
                        onChange={setField('about')}
                        maxLength={ABOUT_MAX}
                        placeholder="What your company does, what you are building, and why someone would want to join…"
                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <p className={cn(
                        'text-right text-[11px] tabular-nums',
                        form.about.length > ABOUT_MAX - 200 ? 'text-amber-600' : 'text-muted-foreground',
                    )}>
                        {form.about.length.toLocaleString()} / {ABOUT_MAX.toLocaleString()}
                    </p>
                </div>
            </section>

            {/* ── Details ───────────────────────────────────────────────── */}
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Details</h3>
                    <p className="text-xs text-muted-foreground">All optional — fill in what is useful to applicants.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field id="website" label="Website" icon={Globe} hint="e.g. acme.com">
                        <Input id="website" value={form.website} onChange={setField('website')}
                            placeholder="acme.com" className="h-11" />
                    </Field>

                    <Field id="linkedin" label="LinkedIn" icon={Linkedin}>
                        <Input id="linkedin" value={form.linkedIn} onChange={setField('linkedIn')}
                            placeholder="linkedin.com/company/acme" className="h-11" />
                    </Field>

                    <Field id="industry" label="Industry" icon={Building2}>
                        <Input id="industry" value={form.industry} onChange={setField('industry')}
                            placeholder="e.g. SaaS, Fintech, Healthcare" className="h-11" />
                    </Field>

                    <Field id="company-size" label="Company size" icon={Users}>
                        <select
                            id="company-size"
                            value={form.companySize}
                            onChange={setField('companySize')}
                            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                            {COMPANY_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </Field>

                    <Field id="location" label="Location" icon={MapPin}>
                        <Input id="location" value={form.location} onChange={setField('location')}
                            placeholder="e.g. Bengaluru, India" className="h-11" />
                    </Field>

                    <Field id="founded" label="Founded" icon={Calendar}>
                        <Input id="founded" type="number" value={form.foundedYear} onChange={setField('foundedYear')}
                            placeholder="e.g. 2018" min={1800} max={new Date().getFullYear()} className="h-11" />
                    </Field>

                    <Field id="contact-email" label="Public contact email" icon={Mail}
                        hint="Where candidates can reach you with questions.">
                        <Input id="contact-email" type="email" value={form.publicContactEmail}
                            onChange={setField('publicContactEmail')} placeholder="careers@acme.com" className="h-11" />
                    </Field>

                    <Field id="public-phone" label="Public phone" icon={Phone}>
                        <Input id="public-phone" type="tel" value={form.publicPhone}
                            onChange={setField('publicPhone')} placeholder="+91 98765 43210" className="h-11" />
                    </Field>
                </div>
            </section>

            {/* ── Save bar ──────────────────────────────────────────────── */}
            <div className="sticky bottom-0 -mx-1 flex items-center justify-between gap-3 border-t border-border bg-background/95 px-1 py-3 backdrop-blur">
                <p className="text-[11px] text-muted-foreground">
                    {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
                </p>
                <Button onClick={handleSave} disabled={saving || !dirty} className="gap-1.5">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save changes
                </Button>
            </div>
          </div>

          {/* ── Live preview ────────────────────────────────────────────── */}
          <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[19rem]">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  <Eye className="h-3 w-3" />Live preview
              </p>
              <JobPagePreview companyName={companyName} form={form} logoUrl={logoUrl} />
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  Updates as you type. This is the company block candidates see at the
                  top of every job posting.
              </p>
          </aside>
        </div>
    );
}
